import React from 'react';
import { withTranslation } from 'react-i18next';
import { Paperclip, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { uploadFile } from '../services/rest';
import { RAG_INDEXABLE_EXTENSIONS, hasRagIndexableExtension } from 'data-types';

interface IUploadedFile {
  name: string;
  fileKey: string;
  type: string;
  size: number;
}

class FileUploadButtonBase extends React.Component<IFileUploadButtonProps, IFileUploadButtonState> {
  private fileInputRef: React.RefObject<HTMLInputElement>;

  constructor(props: IFileUploadButtonProps) {
    super(props);
    this.fileInputRef = React.createRef();
    this.state = {
      isUploading: false,
      uploadedFiles: [],
    };
    // Expose click handler to parent if callback provided
    if (props.onTriggerClick) {
      props.onTriggerClick(this.handleFileUploadClick);
    }
  }

  /**
   * Handles file upload button click
   */
  handleFileUploadClick = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  };

  /**
   * Handles file selection from input
   */
  handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const { t, JWT, recordId } = this.props;

    if (!JWT) {
      toast.error('Authentication required for file upload');
      return;
    }

    this.setState({ isUploading: true });

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        if (!hasRagIndexableExtension(file.name)) {
          toast.error(`Unsupported file type: ${file.type || 'unknown'} for file: ${file.name}`);
          return null;
        }

        // Validate file size (API Gateway hard limit is 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
        if (file.size > maxSize) {
          toast.error(t('messages.fileTooLarge', { fileName: file.name, fileSizeMB }));
          return null;
        }

        try {
          const result = await uploadFile({
            JWT,
            file,
            recordId,
            fileName: file.name,
            path: this.props.path,
          });

          toast.success(t('messages.fileUploaded', { fileName: file.name }), { autoClose: 2000 });
          return {
            name: file.name,
            fileKey: result.fileKey,
            type: file.type,
            size: file.size,
          };
        } catch (error: any) {
          console.error('Error uploading file:', error);

          // Show specific error messages based on error type
          if (error.message?.includes('status: 413')) {
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
            toast.error(t('messages.fileTooLarge', { fileName: file.name, fileSizeMB }));
          } else if (error.message?.includes('Max retries exceeded')) {
            toast.error(t('messages.fileUploadRetryFailed', { fileName: file.name }));
          } else {
            toast.error(t('messages.fileUploadFailed', { fileName: file.name, error: error.message }));
          }

          return null;
        }
      });

      const uploadedFiles = (await Promise.all(uploadPromises)).filter((file): file is IUploadedFile => file !== null);

      if (uploadedFiles.length > 0) {
        const newUploadedFiles = [...this.state.uploadedFiles, ...uploadedFiles];
        this.setState({ uploadedFiles: newUploadedFiles });

        if (this.props.onTextualFilesChange) {
          this.props.onTextualFilesChange(newUploadedFiles.map((file) => file.fileKey));
        }
      }
    } catch (error) {
      console.error('Error in file upload process:', error);
      toast.error(t('messages.error'));
    } finally {
      this.setState({ isUploading: false });
    }

    // Clear the input
    if (event.target) {
      event.target.value = '';
    }
  };

  /**
   * Handles file removal
   */
  handleFileRemove = (fileKey: string) => {
    const newUploadedFiles = this.state.uploadedFiles.filter((file) => file.fileKey !== fileKey);
    this.setState({ uploadedFiles: newUploadedFiles });

    if (this.props.onTextualFilesChange) {
      this.props.onTextualFilesChange(newUploadedFiles.map((file) => file.fileKey));
    }
  };

  /**
   * Renders uploaded file buttons
   */
  renderUploadedFiles = () => {
    const { uploadedFiles } = this.state;

    if (uploadedFiles.length === 0) return null;

    return (
      <div className='uploaded-files'>
        {uploadedFiles.map((file) => (
          <button key={file.fileKey} type='button' className='uploaded-file-button' title={`${file.name} (${(file.size / 1024).toFixed(0)} KB)`}>
            <span className='file-name'>{file.name}</span>
            <X
              size={14}
              className='remove-icon'
              onClick={(e) => {
                e.stopPropagation();
                this.handleFileRemove(file.fileKey);
              }}
            />
          </button>
        ))}
      </div>
    );
  };

  render() {
    const { t, className = 'file-upload-button' } = this.props;
    const { isUploading } = this.state;

    return (
      <div className='file-upload-container'>
        <button type='button' onClick={this.handleFileUploadClick} className={className} disabled={isUploading} title={t('records.tooltips.uploadFile')}>
          <Paperclip size={16} />
          {this.state.isUploading && <div className='upload-progress'>{t('messages.uploading')}</div>}
        </button>
        {!this.props.hideUploadedFiles && this.renderUploadedFiles()}
        <input
          ref={this.fileInputRef}
          type='file'
          accept={RAG_INDEXABLE_EXTENSIONS.join(',')}
          onChange={this.handleFileSelect}
          style={{ display: 'none' }}
          multiple
        />
      </div>
    );
  }
}

interface IFileUploadButtonProps {
  t: (key: string, options?: any) => string;
  JWT: string | null;
  recordId?: string;
  className?: string;
  path?: string; // Optional backend rest api path prefix (e.g., 'rag' for RAG uploads)
  onTextualFilesChange?: (fileKeys: string[]) => void;
  hideUploadedFiles?: boolean;
  onTriggerClick?: (triggerFn: () => void) => void; // Callback to expose the click handler to parent
}

interface IFileUploadButtonState {
  isUploading: boolean;
  uploadedFiles: IUploadedFile[];
}

export const FileUploadButton = withTranslation()(FileUploadButtonBase);
